import { first } from 'lodash';
import { uuid } from '@shared/util/uuid';
import {
  Dimensions,
  FileAttachment,
  MBUploadUtilFile,
  MBUploadUtilInterface,
  SelectAttachmentsOptions,
  SelectFilesOptions,
} from './types';

export const BLOCKED_CONTENT_TYPES = ['image/svg+xml'];

function getOrCreateInput(id: string): HTMLInputElement {
  const container = document.getElementById(id) as HTMLInputElement;
  if (!!container) {
    return container;
  }

  const newContainer = document.createElement('input');
  newContainer.setAttribute('id', id);
  document.getElementsByTagName('body')[0].appendChild(newContainer);
  return newContainer;
}

function openFileSelector({
  multiple,
  acceptedContentTypes = [],
}: {
  multiple: boolean;
  acceptedContentTypes?: string[];
}): Promise<File[]> {
  return new Promise<File[]>(resolve => {
    // https://stackoverflow.com/a/40971885
    const input = getOrCreateInput('shared-components-filepicker');
    input.type = 'file';
    input.multiple = multiple;
    input.style.display = 'none';
    // https://stackoverflow.com/a/6225815
    input.accept = acceptedContentTypes.join(',');
    // Note(fant): we reset the value of the input so that
    // it's possible to upload the same file multiple times
    // in a row (eg when sending it in multiple messages).
    // https://stackoverflow.com/a/20552042
    input.value = '';
    // TODO(fant): TS types
    input.onchange = (event: any) => {
      const files: File[] = Array.from<File>(event.target.files).filter(
        (file: File) => !BLOCKED_CONTENT_TYPES.includes(file.type),
      );
      resolve(files);
    };

    input.click();
  });
}

export const MBUploadUtil: MBUploadUtilInterface = {
  async getWebFileDimensions(file: File): Promise<Dimensions | null> {
    const [imageDimensions, videoDimensions]: [Dimensions | null, Dimensions | null] =
      await Promise.all([
        new Promise<Dimensions | null>(resolve => {
          // https://stackoverflow.com/a/13572240/9117995
          const image = new Image();
          image.src = URL.createObjectURL(file);
          image.onerror = () => resolve(null);
          image.onload = () =>
            resolve({
              width: image.naturalWidth,
              height: image.naturalHeight,
            });
        }),
        new Promise<Dimensions | null>(resolve => {
          // https://stackoverflow.com/a/43298842/9117995 (a less ugly version of this)
          const video = document.createElement('video');
          video.src = URL.createObjectURL(file);
          video.onerror = () => resolve(null);
          video.addEventListener('loadedmetadata', () =>
            resolve({
              width: video.videoWidth,
              height: video.videoHeight,
            }),
          );
        }),
      ]);

    if (!!imageDimensions) {
      return imageDimensions;
    }
    if (!!videoDimensions) {
      return videoDimensions;
    }
    return null;
  },

  async fileToAttachment(file: File): Promise<FileAttachment | null> {
    const dimensions = await MBUploadUtil.getWebFileDimensions(file);

    return {
      name: file.name,
      fileKey: uuid(),
      // clean way to get the relative path to a file upload url
      // https://medium.com/@650egor/2d534f8eaaa
      url: URL.createObjectURL(file),
      contentType: file.type,
      // Potentially adds width/height prop. If the results are null, nothing happens
      ...(dimensions ?? { width: 0, height: 0 }),
      size: file.size,
      thumbnails: null,
    };
  },

  async selectAttachments({
    type: _unused,
    multiple,
  }: SelectAttachmentsOptions): Promise<(FileAttachment | null)[]> {
    const files = await openFileSelector({ multiple });
    const ps = files.map(MBUploadUtil.fileToAttachment);
    return Promise.all(ps);
  },

  async selectFiles({
    multiple,
    acceptedContentTypes,
  }: SelectFilesOptions): Promise<MBUploadUtilFile[] | undefined> {
    const files = await openFileSelector({ multiple, acceptedContentTypes });

    const filePs = files.map(async file => {
      const dims = await MBUploadUtil.getWebFileDimensions(file);
      return {
        id: uuid(),
        url: URL.createObjectURL(file),
        name: file.name,
        contentType: file.type,
        size: file.size,
        ...(dims ?? {}),
      };
    });

    return Promise.all(filePs);
  },

  async selectImage(): Promise<MBUploadUtilFile | undefined> {
    const images = await MBUploadUtil.selectFiles({
      multiple: false,
      acceptedContentTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic'],
    });
    return first(images);
  },
};
